import { toOriginalImageUrl } from './gallery-export-layout';

export interface IExportImage {
    dataUrl: string;
    width: number;
    height: number;
}

// Matches the tile background, so transparent product shots look the same as in the preview
// instead of turning black when flattened to JPEG.
const TILE_BACKGROUND: string = '#f5f5f5';
const JPEG_QUALITY: number = 0.92;

/*
 * Loads a product image for the export: the full-size original first, then the URL the
 * preview used. Returns null when neither loads, so the caller can draw a placeholder.
 */
export async function loadExportImage(url: string, maxPixels: number): Promise<IExportImage | null> {
    const candidates: Array<string> = Array.from(new Set([toOriginalImageUrl(url), url].filter(Boolean)));

    for (const candidate of candidates) {
        try {
            return await fetchAsJpeg(candidate, maxPixels);
        } catch (error) {
            // Try the next candidate; the caller reports images that never load.
        }
    }

    return null;
}

async function fetchAsJpeg(url: string, maxPixels: number): Promise<IExportImage> {
    // `no-store`: the preview's <img> loaded these URLs without CORS, and a cached copy of that
    // response carries no Access-Control-Allow-Origin header, which would fail this request.
    const response: Response = await fetch(url, { mode: 'cors', cache: 'no-store' });
    if (!response.ok) {
        throw new Error(`Image request failed with ${response.status}`);
    }

    // Decoding from a blob: URL keeps the canvas untainted, so toDataURL is allowed.
    const objectUrl: string = URL.createObjectURL(await response.blob());
    try {
        const image: HTMLImageElement = await decodeImage(objectUrl);
        if (!image.naturalWidth || !image.naturalHeight) {
            throw new Error('Image has no dimensions');
        }

        // Originals can be several thousand pixels across; ~300 dpi at the largest tile is as
        // sharp as print gets and keeps a 60-item export from running to hundreds of MB.
        const scale: number = Math.min(1, maxPixels / Math.max(image.naturalWidth, image.naturalHeight));
        const canvas: HTMLCanvasElement = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));

        const context: CanvasRenderingContext2D = canvas.getContext('2d');
        context.fillStyle = TILE_BACKGROUND;
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);

        return {
            dataUrl: canvas.toDataURL('image/jpeg', JPEG_QUALITY),
            width: image.naturalWidth,
            height: image.naturalHeight
        };
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}

function decodeImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve: (image: HTMLImageElement) => void, reject: (reason: unknown) => void) => {
        const image: HTMLImageElement = new Image();
        image.onload = (): void => resolve(image);
        image.onerror = reject;
        image.src = src;
    });
}
